import { createFileRoute, Link } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listCrisisAlerts,
  resolveCrisisAlert,
  getCrisisAlertContext,
  listCrisisAlertNotes,
} from "@/lib/crisis-alerts.functions";
import { RISK_LABEL, type CrisisRiskLevel } from "@/lib/crisis-detection";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldAlert,
  ArrowLeft,
  Eye,
  CheckCircle2,
  AlertTriangle,
  MessageSquare,
  Loader2,
  ExternalLink,
  History,
  FileLock2,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin_/crisis-alerts")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Crisis Alerts — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: CrisisAlertsPage,
});

type Status = "all" | "new" | "monitoring" | "reviewed" | "escalated" | "resolved";
type Decision = "monitoring" | "reviewed" | "escalated" | "resolved";
type Risk = "all" | CrisisRiskLevel;

const MIN_NOTE = 20;

const RISK_STYLES: Record<CrisisRiskLevel, string> = {
  yellow: "bg-yellow-100 text-yellow-900 border-yellow-300",
  orange: "bg-orange-100 text-orange-900 border-orange-300",
  red: "bg-red-100 text-red-900 border-red-300",
  critical: "bg-red-600 text-white border-red-700",
};

const STATUS_STYLES: Record<string, string> = {
  new: "bg-red-50 text-red-800 border-red-200",
  monitoring: "bg-yellow-50 text-yellow-800 border-yellow-200",
  reviewed: "bg-emerald-50 text-emerald-800 border-emerald-200",
  escalated: "bg-purple-50 text-purple-800 border-purple-200",
  resolved: "bg-muted text-foreground border-border",
};

const SOURCE_LABEL: Record<string, string> = {
  journal: "Journal entry",
  dream: "Dream entry",
  community_post: "Community post",
  community_comment: "Community comment",
  mood_check: "Mood check-in note",
  reflection: "Reflection",
  assessment: "Assessment",
  other: "Other",
};

const DECISION_COPY: Record<Decision, { label: string; help: string }> = {
  monitoring: {
    label: "Keep monitoring",
    help: "No immediate risk identified, but the member stays on the watch list.",
  },
  reviewed: {
    label: "Reviewed — no further action",
    help: "Assessed and closed. Record why no further action was needed.",
  },
  escalated: {
    label: "Escalate",
    help: "Handed to the escalation pathway or an external service.",
  },
  resolved: {
    label: "Resolved",
    help: "Support was offered or the concern was addressed. Record the outcome.",
  },
};

function CrisisAlertsPage() {
  const [status, setStatus] = useState<Status>("all");
  const [riskLevel, setRiskLevel] = useState<Risk>("all");

  const listFn = useServerFn(listCrisisAlerts);

  const { data, isLoading } = useQuery({
    queryKey: ["crisis-alerts", status, riskLevel],
    queryFn: () => listFn({ data: { status, riskLevel, limit: 100 } }),
  });

  const alerts = data?.alerts ?? [];

  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:py-10">
        <Link
          to="/admin"
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>

        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-700">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-serif text-2xl text-foreground sm:text-3xl">Crisis Alerts</h1>
            <p className="text-sm text-muted-foreground">
              Flagged content from journals, dreams, community, and check-ins. Every decision
              requires a written rationale and is permanently recorded.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <span className="self-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Risk
            </span>
            {(["all", "critical", "red", "orange", "yellow"] as Risk[]).map((r) => (
              <button
                key={r}
                onClick={() => setRiskLevel(r)}
                className={`min-h-10 rounded-full border px-3 py-1 text-xs font-medium transition ${
                  riskLevel === r
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card text-muted-foreground hover:border-foreground/40"
                }`}
              >
                {r === "all" ? "All" : RISK_LABEL[r as CrisisRiskLevel]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="self-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Status
            </span>
            {(["all", "new", "monitoring", "escalated", "reviewed", "resolved"] as Status[]).map(
              (s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`min-h-10 rounded-full border px-3 py-1 text-xs font-medium capitalize transition ${
                    status === s
                      ? "border-foreground bg-foreground text-background"
                      : "border-border bg-card text-muted-foreground hover:border-foreground/40"
                  }`}
                >
                  {s}
                </button>
              ),
            )}
          </div>
        </div>

        {/* List */}
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading alerts…
          </p>
        ) : alerts.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="mt-3 font-serif text-lg">No matching alerts</p>
            <p className="text-sm text-muted-foreground">
              Nothing has been flagged for the current filters.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {alerts.map((a) => (
              <AlertRow key={a.id} alert={a as AlertRecord} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

type AlertRecord = {
  id: string;
  user_id: string;
  source: string;
  source_id: string | null;
  risk_level: CrisisRiskLevel;
  matched_terms: string[];
  content_snippet: string;
  status: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  display_name: string | null;
};

function AlertRow({ alert }: { alert: AlertRecord }) {
  const [open, setOpen] = useState(false);
  const created = new Date(alert.created_at);
  return (
    <li className="rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${RISK_STYLES[alert.risk_level]}`}
            >
              {alert.risk_level === "critical" && <AlertTriangle className="h-3 w-3" />}
              {RISK_LABEL[alert.risk_level]}
            </span>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_STYLES[alert.status] ?? ""}`}
            >
              {alert.status}
            </span>
            <span className="text-xs text-muted-foreground">
              {SOURCE_LABEL[alert.source] ?? alert.source}
            </span>
          </div>
          <div className="mt-2 text-sm text-foreground/90">
            <span className="font-medium">{alert.display_name ?? "Anonymous member"}</span>
            <span className="text-muted-foreground"> · {created.toLocaleString()}</span>
          </div>
          <p className="mt-2 text-sm italic text-foreground/70">
            Safeguarding alert — content is private and not shown to admins.
          </p>
          {alert.matched_terms.length > 0 && (
            <div className="mt-2 text-xs text-muted-foreground">
              {alert.matched_terms.length} trigger
              {alert.matched_terms.length === 1 ? "" : "s"} detected
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setOpen((o) => !o)}
            className="inline-flex min-h-10 items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            <Eye className="h-3.5 w-3.5" /> {open ? "Hide review" : "Review"}
          </button>
          <Link
            to="/admin/users/$userId"
            params={{ userId: alert.user_id }}
            className="inline-flex min-h-10 items-center gap-1 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            <ExternalLink className="h-3.5 w-3.5" /> View user
          </Link>
        </div>
      </div>

      {open && <ReviewPanel alert={alert} />}
    </li>
  );
}

function ReviewPanel({ alert }: { alert: AlertRecord }) {
  const contextFn = useServerFn(getCrisisAlertContext);
  const resolveFn = useServerFn(resolveCrisisAlert);
  const qc = useQueryClient();

  const [decision, setDecision] = useState<Decision | null>(null);
  const [note, setNote] = useState("");
  const [contacted, setContacted] = useState(false);

  const { data: ctx, isLoading } = useQuery({
    queryKey: ["crisis-alert-context", alert.id],
    queryFn: () => contextFn({ data: { id: alert.id } }),
  });

  const record = useMutation({
    mutationFn: () =>
      resolveFn({
        data: {
          id: alert.id,
          decision: decision as Decision,
          note: note.trim(),
          memberContacted: contacted,
        },
      }),
    onSuccess: (res) => {
      setNote("");
      setDecision(null);
      setContacted(false);
      qc.invalidateQueries({ queryKey: ["crisis-alerts"] });
      qc.invalidateQueries({ queryKey: ["crisis-alert-context", alert.id] });
      qc.invalidateQueries({ queryKey: ["crisis-alert-notes", alert.id] });
      qc.invalidateQueries({ queryKey: ["crisis-summary"] });
      toast.success(`Decision recorded — ${res.from} → ${res.to}`);
    },
    onError: (e) =>
      toast.error(e instanceof Error ? e.message : "Could not record the decision"),
  });

  const noteOk = note.trim().length >= MIN_NOTE;
  const canSubmit = Boolean(decision) && noteOk && !record.isPending;

  return (
    <div className="space-y-4 border-t border-border/60 px-4 pb-4 pt-4">
      {/* Evidence */}
      <section className="rounded-xl border border-border/60 bg-muted/30 p-3">
        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <FileLock2 className="h-3.5 w-3.5" /> Evidence
        </h3>
        {isLoading ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Gathering context…
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <dl className="space-y-1 text-sm">
              <Row label="Risk level" value={RISK_LABEL[alert.risk_level]} />
              <Row label="Source" value={SOURCE_LABEL[alert.source] ?? alert.source} />
              <Row
                label="Triggers detected"
                value={`${alert.matched_terms.length} term${alert.matched_terms.length === 1 ? "" : "s"}`}
              />
              <Row label="Raised" value={new Date(alert.created_at).toLocaleString()} />
              <Row
                label="Last reviewed"
                value={
                  alert.reviewed_at ? new Date(alert.reviewed_at).toLocaleString() : "Never"
                }
              />
            </dl>
            <dl className="space-y-1 text-sm">
              <Row
                label="Member"
                value={ctx?.member.display_name ?? ctx?.member.first_name ?? "Anonymous member"}
              />
              <Row label="Membership" value={ctx?.member.membership_status ?? "—"} />
              <Row label="Country" value={ctx?.member.country ?? "—"} />
              <Row
                label="Last sign-in"
                value={
                  ctx?.member.last_login_at
                    ? new Date(ctx.member.last_login_at).toLocaleString()
                    : "—"
                }
              />
              <Row
                label="Prior alerts"
                value={`${ctx?.history.total_prior ?? 0} total · ${ctx?.history.unresolved_prior ?? 0} still open`}
              />
            </dl>
          </div>
        )}
        <p className="mt-3 rounded-lg bg-background/70 p-2 text-xs text-muted-foreground">
          Journal, dream, and mood content is confidential and is never shown here. Offer
          support through the member's profile — do not attempt to read the source content.
        </p>
        {(ctx?.history.recent?.length ?? 0) > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
            {ctx!.history.recent.map((h) => (
              <li key={h.id}>
                {new Date(h.created_at).toLocaleDateString()} ·{" "}
                {RISK_LABEL[h.risk_level as CrisisRiskLevel]} · {h.source} · {h.status}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Decision */}
      <section className="rounded-xl border border-border bg-background p-3">
        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <ShieldAlert className="h-3.5 w-3.5" /> Record a decision
        </h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {(Object.keys(DECISION_COPY) as Decision[]).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDecision(d)}
              aria-pressed={decision === d}
              className={`rounded-xl border p-2.5 text-start transition ${
                decision === d
                  ? "border-foreground bg-foreground/5 ring-1 ring-foreground/20"
                  : "border-border hover:border-foreground/40"
              }`}
            >
              <span className="block text-sm font-medium">{DECISION_COPY[d].label}</span>
              <span className="block text-xs text-muted-foreground">{DECISION_COPY[d].help}</span>
            </button>
          ))}
        </div>

        <label className="mt-3 block text-xs font-medium text-muted-foreground" htmlFor={`note-${alert.id}`}>
          Decision note (required, minimum {MIN_NOTE} characters)
        </label>
        <Textarea
          id={`note-${alert.id}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="What did you assess, what action did you take, and why?"
          className="mt-1"
        />
        <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>{noteOk ? "Ready to record" : `${MIN_NOTE - note.trim().length} more characters needed`}</span>
          <span>{note.trim().length}/2000</span>
        </div>

        <label className="mt-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={contacted}
            onChange={(e) => setContacted(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          <span className="flex items-center gap-1 text-muted-foreground">
            <UserRound className="h-3.5 w-3.5" /> I contacted this member directly
          </span>
        </label>

        <button
          type="button"
          onClick={() => canSubmit && record.mutate()}
          disabled={!canSubmit}
          className="mt-3 inline-flex min-h-10 items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {record.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4" />
          )}
          Record decision
        </button>
        {!decision && (
          <p className="mt-2 text-xs text-muted-foreground">Choose a decision to continue.</p>
        )}
      </section>

      {/* Audit trail */}
      <section>
        <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <History className="h-3.5 w-3.5" /> Audit trail
        </h3>
        {(ctx?.audit?.length ?? 0) === 0 ? (
          <p className="text-xs text-muted-foreground">No decisions recorded yet.</p>
        ) : (
          <ol className="space-y-2">
            {ctx!.audit.map((a) => (
              <li
                key={a.id}
                className="rounded-lg border border-border/60 bg-background p-2.5 text-sm"
              >
                <div className="text-[11px] text-muted-foreground">
                  {a.admin_name ?? "Admin"} · {new Date(a.created_at).toLocaleString()}
                </div>
                <div className="mt-0.5 text-foreground/90">
                  {a.from_status ?? "—"} → <span className="font-medium">{a.to_status}</span>
                  {a.member_contacted && (
                    <span className="ms-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800">
                      member contacted
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <NotesPanel alertId={alert.id} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-end text-sm text-foreground/90">{value}</dd>
    </div>
  );
}

function NotesPanel({ alertId }: { alertId: string }) {
  const listFn = useServerFn(listCrisisAlertNotes);
  const { data } = useQuery({
    queryKey: ["crisis-alert-notes", alertId],
    queryFn: () => listFn({ data: { alertId } }),
  });

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <MessageSquare className="h-3.5 w-3.5" /> Decision notes
      </h3>
      <ul className="space-y-2">
        {(data?.notes ?? []).map((n) => (
          <li key={n.id} className="rounded-lg border border-border/60 bg-background p-3 text-sm">
            <div className="mb-1 text-[11px] text-muted-foreground">
              {n.admin_name ?? "Admin"} · {new Date(n.created_at).toLocaleString()}
            </div>
            <p className="whitespace-pre-wrap break-words text-foreground/90">{n.note}</p>
          </li>
        ))}
        {(!data?.notes || data.notes.length === 0) && (
          <li className="text-xs text-muted-foreground">No notes yet.</li>
        )}
      </ul>
    </section>
  );
}
